import type {ComboboxData} from '@mantine/core';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useEffect, useMemo, useState} from 'react';
import type {DeviceSimpleResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {
  ActiveInactivePills,
  VerificationPills,
} from 'src/design/atoms/pillPresets';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {
  ActiveStateDataOptions,
  VerifiedStateDataOptions,
} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {
  useDeleteDeviceMutation,
  useGetDeviceListV2Query,
} from 'src/redux/queries/device';
import {useGetEquipmentTypeSelectOptionsQuery} from 'src/redux/queries/equipmentType';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  useVerifyDevice,
  VerifyDeviceDialogCopy,
} from 'src/pages/Modules/Inspections/shared/useVerifyDevice';
import {ImportDevicesModal} from './ImportDevicesModal';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface DeviceTableProps {
  /** Filters the list to devices belonging to this site (e.g. embedded on SiteView). */
  siteId?: number;
  /** Distinguishes persisted table state (column order/filters) across embeddings. */
  identifier?: string;
  /** Renders the help line above the table. Off when embedded within another entity's view. */
  showHelp?: boolean;
}

// No distinct-values endpoint exists for the free-text location fields, so filter options are
// derived from the rows currently loaded rather than the full dataset.
const uniqueOptions = (values: (string | undefined)[]): ComboboxData =>
  Array.from(new Set(values.filter((v): v is string => !!v))).map(v => ({
    value: v,
    label: v,
  }));

export const DeviceTable: React.FC<DeviceTableProps> = ({
  siteId,
  identifier = 'device',
  showHelp = true,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const inspectionPerms = useInspectionPermissions();
  const [deleteTarget, setDeleteTarget] = useState<DeviceSimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<DeviceSimpleResponse>();
  const [confirmVerifyOpened, confirmVerifyHandlers] = useDisclosure(false);
  const [importOpened, importHandlers] = useDisclosure(false);

  const [deleteDevice] = useDeleteDeviceMutation();
  const {verify, isVerifying} = useVerifyDevice();

  const {data: equipmentTypes} = useGetEquipmentTypeSelectOptionsQuery({
    start: 0,
    length: 10000,
    filters: [],
    filterFns: [],
    sorting: [],
  });

  const showSiteColumn = siteId === undefined;

  const equipmentTypeFilterData = useMemo<ComboboxData>(
    () =>
      (equipmentTypes?.data ?? []).map(option => ({
        value: String(option.id),
        label: option.label ?? String(option.id),
      })),
    [equipmentTypes]
  );

  const [buildingFilterData, setBuildingFilterData] = useState<ComboboxData>(
    []
  );
  const [floorFilterData, setFloorFilterData] = useState<ComboboxData>([]);
  const [roomFilterData, setRoomFilterData] = useState<ComboboxData>([]);

  const columns = useMemo<MRT_ColumnDef<DeviceSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'isVerified',
        header: 'Verification',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: VerifiedStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...VerificationPills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'serialNumber',
        header: 'Serial Number',
      },
      ...(showSiteColumn
        ? [
            {
              accessorKey: 'siteName',
              header: 'Site',
            } as MRT_ColumnDef<DeviceSimpleResponse>,
          ]
        : []),
      {
        id: 'equipmentTypeId',
        accessorFn: row => String(row.equipmentTypeId),
        header: 'Equipment Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: equipmentTypeFilterData,
        },
        Cell: ({row}) => row.original.equipmentTypeName,
      },
      {
        accessorKey: 'building',
        header: 'Building',
        enableColumnFilterModes: false,
        enableGrouping: true,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: buildingFilterData,
        },
      },
      {
        accessorKey: 'floor',
        header: 'Floor',
        enableColumnFilterModes: false,
        enableGrouping: true,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: floorFilterData,
        },
      },
      {
        accessorKey: 'room',
        header: 'Room',
        enableColumnFilterModes: false,
        enableGrouping: true,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: roomFilterData,
        },
      },
    ],
    [
      showSiteColumn,
      equipmentTypeFilterData,
      buildingFilterData,
      floorFilterData,
      roomFilterData,
    ]
  );

  const tableState = useTableState({
    identifier,
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: ['true'],
      },
    ],
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetDeviceListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
      siteId,
    });
  const rows = useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.totalRecords ?? 0;

  useEffect(() => {
    setBuildingFilterData([]);
    setFloorFilterData([]);
    setRoomFilterData([]);
  }, [siteId]);

  useEffect(() => {
    setBuildingFilterData(prev => mergeOptions(prev, rows, r => r.building));
    setFloorFilterData(prev => mergeOptions(prev, rows, r => r.floor));
    setRoomFilterData(prev => mergeOptions(prev, rows, r => r.room));
  }, [rows]);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteDevice(deleteTarget.id).unwrap();
      setAlert(
        `${deleteTarget.name ?? 'Device'} was successfully deleted.`,
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  const handleVerify = async () => {
    if (!verifyTarget) return;

    confirmVerifyHandlers.close();
    await verify(verifyTarget.id, verifyTarget.name);
    setVerifyTarget(undefined);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableGrouping: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => navigate(PathUtility.DevicesView(row.original.id))}
          />
          <TableRowActions.Menu>
            {!row.original.isVerified && inspectionPerms.update && (
              <TableRowActions.MenuItem
                onClick={() => {
                  setVerifyTarget(row.original);
                  confirmVerifyHandlers.open();
                }}>
                Verify
              </TableRowActions.MenuItem>
            )}
            {inspectionPerms.delete && (
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            )}
          </TableRowActions.Menu>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {inspectionPerms.create && (
              <TableToolbar.Action
                variant="create"
                onClick={() =>
                  navigate(
                    siteId !== undefined
                      ? `${PathUtility.DevicesCreate()}?siteId=${siteId}`
                      : PathUtility.DevicesCreate()
                  )
                }>
                New Device
              </TableToolbar.Action>
            )}
            {inspectionPerms.import && (
              <TableToolbar.Action
                variant="import"
                onClick={() => importHandlers.open()}>
                Import
              </TableToolbar.Action>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {showHelp && (
        <Text size="sm" c="dimmed" mb="sm">
          Manage the per-site equipment inventory.
        </Text>
      )}
      {table}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title="Are you sure you want to delete this device?"
        body="This action cannot be undone."
      />

      <ConfirmDialog
        opened={confirmVerifyOpened}
        onClose={() => confirmVerifyHandlers.close()}
        onConfirm={handleVerify}
        confirmationButtonColor="green"
        confirmationButtonDisabled={isVerifying}
        title={VerifyDeviceDialogCopy.title}
        body={VerifyDeviceDialogCopy.body}
      />

      <ImportDevicesModal
        opened={importOpened}
        onClose={() => importHandlers.close()}
        siteId={siteId}
      />
    </>
  );
};

function mergeOptions(
  prev: ComboboxData,
  rows: DeviceSimpleResponse[],
  pick: (row: DeviceSimpleResponse) => string | undefined
): ComboboxData {
  const prevValues = new Set(
    (prev as {value: string}[]).map(option => option.value)
  );
  const next = uniqueOptions(rows.map(pick)).filter(
    option => !prevValues.has((option as {value: string}).value)
  );
  return next.length === 0 ? prev : [...prev, ...next];
}
